import { createSignal, Show, type JSX } from 'solid-js';
import { useSearchParams } from '@solidjs/router';
import { authStore } from '@stores/session';
import PageHead from '@components/seo/PageHead';

export default function SignInRoute(): JSX.Element {
  const [params] = useSearchParams<{ return?: string }>();
  const [email, setEmail] = createSignal('');
  const [sent, setSent] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [submitting, setSubmitting] = createSignal(false);

  // `?return=<path>` handoff — pass the path through to the magic-
  // link redirect so the user lands on their original destination
  // (e.g. /shared-script?strains=...) after clicking the email link.
  // Same-origin enforced inside signInWithMagicLink.
  function returnPath(): string | undefined {
    const r = params.return;
    return r && r.startsWith('/') ? r : undefined;
  }

  async function submit(e: Event) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: err } = await authStore.signInWithMagicLink(email(), returnPath());
    setSubmitting(false);
    if (err) {
      setError(err.message);
      return;
    }
    setSent(true);
  }

  return (
    <section class="px-6 py-16 max-w-md mx-auto">
      <PageHead
        title="Sign in · Terp Track"
        description="Sign in to Terp Track with a passwordless magic link. No name, no postcode, no prescription details collected."
        canonical="/sign-in"
      />
      <h1 class="font-display text-3xl font-bold">Sign in</h1>
      <p class="mt-2 text-sm text-brand-green/80">
        We use passwordless magic links. We do not collect your name, postcode, or any
        prescription details &ndash; only your email, so we can recognise your private diary.
      </p>

      <Show
        when={!sent()}
        fallback={
          <div class="mt-6 rounded-lg bg-brand-hex/60 p-4 text-sm">
            Check your inbox &ndash; we've sent a sign-in link to <strong>{email()}</strong>.
          </div>
        }
      >
        <form class="mt-6 flex flex-col gap-3" onSubmit={submit}>
          <label class="text-sm font-medium" for="email">
            Email address
          </label>
          <input
            id="email"
            type="email" name="email"
            required
            autocomplete="email"
            value={email()}
            onInput={(e) => setEmail(e.currentTarget.value)}
            class="rounded-md border border-brand-hex px-3 py-2 bg-white focus:border-brand-moss"
          />
          <button
            type="submit"
            disabled={submitting()}
            class="mt-2 rounded-full bg-brand-green text-brand-cream py-2 font-medium disabled:opacity-60"
          >
            {submitting() ? 'Sending…' : 'Send magic link'}
          </button>
          <Show when={error()}>
            <p class="text-sm text-red-700" role="alert">
              {error()}
            </p>
          </Show>
        </form>
      </Show>
    </section>
  );
}
